import { Box, type SxProps, type Theme } from "@mui/material";
import type { ElementType, FC, ReactNode } from "react";
import {
	LEDGER_BODY_SX,
	LEDGER_LEADING_SX,
	LEDGER_UNIT_SX,
	type LedgerLineVariant,
	ledgerAmountSx,
	ledgerLabelSx,
	ledgerLineSx,
	ledgerRateSx,
	sharesLabelColumn,
} from "./ledgerLineSx";

export type { LedgerLineVariant };

type Props = {
	/** The line's name. Already translated, or a backend label shown as written; may be a link. */
	label: ReactNode;
	/** Secondary text after the label (a time range, a rule's scope). Wraps under the label when narrow. */
	detail?: ReactNode;
	/** A glyph before the label. Items only — component lines draw the rail in this slot. */
	leading?: ReactNode;
	/** The secondary figure column (a rate). Right-aligned, tabular. */
	rate?: ReactNode;
	/**
	 * The rate's unit ("/h"), in a slot every line with a rate reserves — so a unit-less component figure
	 * still lines its digits up under the item's "$55.96/h". Already translated.
	 */
	rateUnit?: string;
	/** The primary figure column (the line's amount). Right-aligned, tabular. */
	amount?: ReactNode;
	/** DEFAULT: `item` — a line with no stated role is a line of the sum itself. */
	variant?: LedgerLineVariant;
	/**
	 * Colour for the figures when the line carries a domain signal the reader should catch while scanning
	 * the column (an overtime premium). Pass a theme/token colour (e.g. `OVERTIME.INK`), never a literal.
	 */
	figureColor?: string;
	/** DEFAULT: `"li"` — a ledger is a list; wrap a run of lines in a `<ul>`. Pass `"div"` inside another `<li>`. */
	component?: ElementType;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * One line of an itemized sum — a wage breakdown segment and the rules that built its rate, an invoice
 * line and its surcharges, a cost and what it is made of.
 *
 * The figure columns have fixed widths (`LEDGER` tokens, in `rem` so lines of different sizes share them),
 * so every line in a list aligns its figures without measuring its neighbours: an item's amounts and the
 * components beneath it read down one column, which is what lets the reader check that the parts add up.
 * Label and detail take the remaining width and wrap; the figures never do.
 */
export const LedgerLine: FC<Props> = ({ label, detail, leading, rate, rateUnit, amount, variant = "item", figureColor, component = "li", dataTestId, sx }) => {
	const componentLike = variant === "component";
	return (
		<Box component={component} data-testid={dataTestId} data-variant={variant} sx={[ledgerLineSx(variant), ...(Array.isArray(sx) ? sx : [sx])]}>
			<Box aria-hidden={componentLike || undefined} sx={LEDGER_LEADING_SX}>
				{componentLike ? null : leading}
			</Box>
			<Box sx={LEDGER_BODY_SX}>
				<Box component="span" sx={ledgerLabelSx(variant)}>
					{label}
				</Box>
				{detail !== undefined && (
					<>
						{!sharesLabelColumn(variant) && (
							<Box component="span" aria-hidden sx={{ color: "text.disabled", mx: 0.75 }}>
								·
							</Box>
						)}
						<Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
							{detail}
						</Box>
					</>
				)}
			</Box>
			<Box sx={ledgerRateSx(variant, figureColor)}>
				{rate}
				{rate !== undefined && (
					<Box component="span" sx={LEDGER_UNIT_SX}>
						{rateUnit}
					</Box>
				)}
			</Box>
			<Box sx={ledgerAmountSx(variant, figureColor)}>{amount}</Box>
		</Box>
	);
};
